import type { components } from '../../api/generated/schema'

type Schema = components['schemas']

/**
 * The typed section map for the character sheet — the hand-written layer the
 * implementation plan §2.2 warned about.
 *
 * `CharacterSheetDto.sections` is `IReadOnlyDictionary<string, object>` on the
 * wire, so `openapi-typescript` can only emit `Record<string, unknown>`. This maps
 * each wire key to the shape the server actually puts there.
 *
 * **Ten of the fourteen come from the generated schema.** They are in the OpenAPI
 * document only because `EncounterCardDto` happens to reference them as real
 * properties. The other four — `core`, `bio`, `aspects`, `fate` — appear on no
 * card and so on no operation, which means OpenAPI never sees them and they are
 * transcribed by hand below from
 * `CampaignManager.Api/Contracts/Characters/SectionDtos.cs`.
 *
 * Those four are therefore the only types here that can silently drift from the
 * API. Making them reachable from an operation — a typed sections schema, or a
 * `[ProducesResponseType]` that names them — would delete this hand-written block
 * and put all fourteen under the CI drift check.
 */
export interface CharacterSections {
  core: CoreSection
  bio: BioSection
  aspects: AspectsSection
  fate: FateSection
  skills: Schema['SkillsSectionDto']
  stress: Schema['StressSectionDto']
  consequences: Schema['ConsequencesSectionDto']
  abilities: Schema['AbilitiesSectionDto']
  shapes: Schema['ShapesSectionDto']
  specializations: Schema['SpecializationsSectionDto']
  focusItems: Schema['FocusItemsSectionDto']
  enchantedItems: Schema['EnchantedItemsSectionDto']
  roteSpells: Schema['RoteSpellsSectionDto']
  equipment: Schema['EquipmentSectionDto']
}

// ---------------------------------------------------------------------------
// Hand-transcribed sections. Keep in step with SectionDtos.cs.
// ---------------------------------------------------------------------------

/** Identity and capacity. Section key `core`. */
export interface CoreSection {
  id: number
  name: string
  type: Schema['CharacterType']
  classType: Schema['CharacterClassType']
  totalRefresh: number
  /**
   * The character's refresh capacity — identical to `totalRefresh`. It used to be that plus a
   * character level, which the system has no such thing as; the server keeps the name because
   * it is the shape of the contract, not because there is a level in it.
   */
  refreshLevel: number
  /**
   * Whether this character has a magic half at all. Decided server-side from the class type
   * and the abilities, so the sheet shows the four magic cards on this one flag rather than
   * re-deciding from the ability list.
   */
  hasSpellcasting: boolean
  /** Which disciplines the abilities grant, combined. `None` for a caster who has taken none. */
  spellcasting: Schema['SpellcastingType']
}

/** Fate points and the refresh budget. Section key `fate`. */
export interface FateSection {
  fatePoints: number
  refreshLevel: number
  refreshUsed: number
  refreshAvailable: number
  isOverLimit: boolean
}

/**
 * Bio. Already visibility-filtered server-side — hidden fields arrive null, and
 * the client must not try to distinguish "hidden" from "empty". Section key `bio`.
 */
export interface BioSection {
  fullName: string | null
  nickname: string | null
  age: number | null
  backstory: string | null
  firstEncounter: string | null
  characterDescription: string | null
  portraitUrl: string | null
  portraitUrlSmall: string | null
  portraitUrlOriginal: string | null
  visibility: Schema['BioVisibilityDto']
}

/**
 * Aspects arrive as named slots rather than a list, and are visibility-filtered
 * the same way. Section key `aspects`.
 */
export interface AspectsSection {
  highConcept: string | null
  trouble: string | null
  aspect1: string | null
  aspect2: string | null
  aspect3: string | null
  aspect4: string | null
  aspect5: string | null
  aspect6: string | null
  tempAspect1: string | null
  tempAspect2: string | null
  tempAspect3: string | null
  visibility: Schema['AspectsVisibilityDto']
}

// ---------------------------------------------------------------------------
// The sheet
// ---------------------------------------------------------------------------

export type CharacterSummary = Schema['CharacterSummaryDto']

export interface CharacterSheet {
  characterId: number
  version: number
  /** The shape scope these sections belong to; null for a single-shape character. */
  shapeId: number | null
  isOwner: boolean
  isGameMaster: boolean
  /**
   * Whether *this reader* has hidden the character from their own lists. A fact about the
   * reader, like `isOwner` — which is why it is here and not in a section: two people reading
   * the same sheet get different answers, and the envelope patches sections, not opinions.
   */
  isHidden: boolean
  sections: Partial<CharacterSections>
}

/**
 * Narrows the wire DTO into the typed sheet. This is the single place the untyped
 * `sections` dictionary is given a type, so it is also the only place to look when
 * a section reads as `undefined` that should not.
 */
export function toCharacterSheet(dto: Schema['CharacterSheetDto']): CharacterSheet {
  if (dto.characterId === undefined) {
    throw new Error('The API returned a character sheet with no id.')
  }

  return {
    characterId: dto.characterId,
    version: dto.version ?? 0,
    shapeId: dto.shapeId ?? null,
    isOwner: dto.isOwner ?? false,
    isGameMaster: dto.isGameMaster ?? false,
    isHidden: dto.isHidden ?? false,
    sections: (dto.sections ?? {}) as Partial<CharacterSections>,
  }
}

// ---------------------------------------------------------------------------
// Presentation helpers — layout only. Nothing here derives a game value; every
// number on the sheet is computed server-side (architecture §3).
// ---------------------------------------------------------------------------

export interface AspectEntry {
  key: string
  text: string
  /** Temporary aspects are the ones a scene put there, and read as less permanent. */
  temporary: boolean
}

/**
 * Flattens the named aspect slots into the chip list the design spec asks for
 * (§5.1), skipping High Concept and Trouble — those get their own lines — and
 * dropping empty slots.
 */
export function aspectChips(aspects: AspectsSection | undefined): AspectEntry[] {
  if (aspects === undefined) return []

  const slots: Array<[string, string | null, boolean]> = [
    ['aspect1', aspects.aspect1, false],
    ['aspect2', aspects.aspect2, false],
    ['aspect3', aspects.aspect3, false],
    ['aspect4', aspects.aspect4, false],
    ['aspect5', aspects.aspect5, false],
    ['aspect6', aspects.aspect6, false],
    ['tempAspect1', aspects.tempAspect1, true],
    ['tempAspect2', aspects.tempAspect2, true],
    ['tempAspect3', aspects.tempAspect3, true],
  ]

  return slots
    .filter(([, text]) => text !== null && text.trim() !== '')
    .map(([key, text, temporary]) => ({ key, text: text as string, temporary }))
}

export interface SkillRung {
  levelValue: number
  /** The enum member name doubles as the Fate ladder label ("Superb", "Great"…). */
  label: string
  skills: Array<{ id: number; name: string }>
}

/**
 * Groups skills into pyramid rungs, highest first.
 *
 * This is layout, not derivation: the level and its numeric value both arrive on
 * every entry, and the pyramid's *validity* is decided server-side and shipped in
 * `skills.pyramid`.
 */
export function skillRungs(skills: Schema['SkillsSectionDto'] | undefined): SkillRung[] {
  const entries = skills?.skills ?? []
  const byLevel = new Map<number, SkillRung>()

  for (const entry of entries) {
    const levelValue = entry.levelValue ?? 0
    let rung = byLevel.get(levelValue)
    if (rung === undefined) {
      rung = { levelValue, label: String(entry.level ?? ''), skills: [] }
      byLevel.set(levelValue, rung)
    }
    rung.skills.push({ id: entry.skillId ?? 0, name: entry.name ?? '' })
  }

  for (const rung of byLevel.values()) {
    rung.skills.sort((a, b) => a.name.localeCompare(b.name))
  }

  return [...byLevel.values()].sort((a, b) => b.levelValue - a.levelValue)
}

/** The lowest rung a skill can be taken at. Mediocre (+0) means "not taken". */
export const LOWEST_RUNG = 1

/**
 * Every rung a character has to fill, highest first: from its cap down to Average (+1),
 * each carrying the skills currently on it — empty ones included.
 *
 * This is what makes adding a skill an explicit act rather than a side effect of setting a
 * level: the shape of the pyramid comes from the character's `maxSkillLevel`, so the editor
 * can offer exactly the rungs that exist and nothing else.
 *
 * A rung above the cap appears only when a skill is already up there, which a cap lowered
 * behind the client's back can leave behind. Stopping at the cap would hide the skill.
 */
export function skillLadder(skills: Schema['SkillsSectionDto'] | undefined): SkillRung[] {
  if (skills === undefined) return []

  const held = new Map(skillRungs(skills).map((rung) => [rung.levelValue, rung]))
  const top = Math.max(skills.maxLevelValue ?? 0, ...held.keys(), LOWEST_RUNG)

  const ladder: SkillRung[] = []
  for (let levelValue = top; levelValue >= LOWEST_RUNG; levelValue--) {
    ladder.push(held.get(levelValue) ?? { levelValue, label: '', skills: [] })
  }

  return ladder
}

/** Refresh costs are spends, so a positive cost reads as a minus on the budget. */
export function formatRefresh(cost: number): string {
  if (cost === 0) return 'free'
  return cost > 0 ? `−${cost}` : `+${Math.abs(cost)}`
}

/** A discipline a specialization can be bought in — the castable half of `SpellcastingType`. */
export type Discipline = 'Evocation' | 'Thaumaturgy'

/** Splits the combined `SpellcastingType` back into the disciplines it stands for. */
export function disciplinesOf(spellcasting: Schema['SpellcastingType'] | undefined): Discipline[] {
  if (spellcasting === 'Both') return ['Evocation', 'Thaumaturgy']
  if (spellcasting === 'Evocation' || spellcasting === 'Thaumaturgy') return [spellcasting]
  return []
}

/**
 * Which discipline an existing specialization belongs to.
 *
 * Everything written through the sheet carries one discipline, but a reader has to place
 * older rows too — `Both`, or a missing type — so a set element decides when the type does
 * not. Shared between the screen card and the printed sheet: two copies of this would be two
 * chances to file a Transformation specialization under Evocation.
 */
export function specializationDiscipline(entry: Schema['SpecializationDto']): Discipline {
  if (entry.spellcastingType === 'Thaumaturgy') return 'Thaumaturgy'
  if (entry.spellcastingType === 'Evocation') return 'Evocation'

  const thaumaturgy = entry.thaumaturgyElement
  return thaumaturgy !== undefined && thaumaturgy !== 'None' ? 'Thaumaturgy' : 'Evocation'
}

/** The element a specialization reads as — both, joined, for a row that carries each. */
export function specializationElement(entry: Schema['SpecializationDto']): string {
  const evocation = String(entry.evocationElement ?? '')
  const thaumaturgy = String(entry.thaumaturgyElement ?? '')
  if (entry.spellcastingType === 'Thaumaturgy') return thaumaturgy
  if (entry.spellcastingType === 'Evocation') return evocation
  return [evocation, thaumaturgy].filter((value) => value !== '' && value !== 'None').join(' · ')
}

/** Maps a consequence level onto the design system's slot styling. */
export function consequenceLevelName(level: string | undefined): 'mild' | 'moderate' | 'severe' {
  const normalized = (level ?? '').toLowerCase()
  if (normalized === 'mild') return 'mild'
  if (normalized === 'moderate') return 'moderate'
  // Severe and Extreme both read as the heaviest slot; Extreme keeps its own label.
  return 'severe'
}
